import { useQuery } from '@tanstack/react-query';
import {
  createConcussionRepo,
  CRX_RELEASE_DATE
} from 'features/patient/_utils/patient-info/patientInfoSlice';
import { CgsInventory } from 'features/patient/assessment/questionnaires/cgs/cgsSlice';
import { DhiInventory } from 'features/patient/assessment/questionnaires/dhi/dhiSlice';
import { PcsInventory } from 'features/patient/assessment/questionnaires/pcs/pcsSlice';
import { PromisInventory } from 'features/patient/assessment/questionnaires/promis/promisSlice';
import { createRepo } from 'lib/api/firebase/create-repo';
import { Inventory } from 'lib/documents';
import { Collection, PatientSubcollection } from 'lib/documents/document';
import { queryKeyFactory } from 'lib/utils/queryKeyFactory';

/**
 * A generic function to generate queries for Inventories
 * @param path
 * @param queryKey
 * @returns
 */
function useInventoryQueryFactory<T extends Inventory>(
  path: string,
  queryKey: string[]
) {
  const repo = createRepo<T>(path);
  return useQuery({
    queryKey,
    queryFn: () =>
      repo
        .getList({ orderBy: ['date', 'asc'] })
        .then(_ => _.filter(_ => _.date.isAfter(CRX_RELEASE_DATE)))
  });
}

export function useGetCGSInfo(patientId: string) {
  return useInventoryQueryFactory<CgsInventory>(
    `${Collection.PatientAssessments}/${patientId}/${PatientSubcollection.InventoriesCgs}`,
    queryKeyFactory.cgs(patientId)
  );
}

export function useGetPCSInfo(patientId: string) {
  return useInventoryQueryFactory<PcsInventory>(
    `${Collection.PatientAssessments}/${patientId}/${PatientSubcollection.InventoriesPcs}`,
    queryKeyFactory.pcs(patientId)
  );
}

export function useGetDHIInfo(patientId: string) {
  return useInventoryQueryFactory<DhiInventory>(
    `${Collection.PatientAssessments}/${patientId}/${PatientSubcollection.InventoriesDhi}`,
    queryKeyFactory.dhi(patientId)
  );
}

export function useGetPromisInfo(patientId: string) {
  return useInventoryQueryFactory<PromisInventory>(
    `${Collection.PatientAssessments}/${patientId}/${PatientSubcollection.InventoriesPromis}`,
    queryKeyFactory.promis(patientId)
  );
}

export function useGetImPACTInfo(patientId: string) {
  return useInventoryQueryFactory<PcsInventory>(
    `${Collection.PatientAssessments}/${patientId}/${PatientSubcollection.InventoriesImpact}`,
    queryKeyFactory.impact(patientId)
  );
}

export function useGetConcussions(patientId: string) {
  const concussionRepo = createConcussionRepo(patientId);
  return useQuery({
    queryKey: queryKeyFactory.concussions(patientId),
    queryFn: () =>
      concussionRepo.getList({
        orderBy: ['date', 'asc']
      })
  });
}
